import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Add, Pencil, Reset, Settings } from '@factorialco/f0-react/icons/app'
import { TrainingDetailRouteQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ContentTypeDialog from 'modules/trainings/components/Revamp/TrainingsDetail/ContentTypeDialog'

type Training = NonNullable<TrainingDetailRouteQuery['trainings']['training']>

type Props = {
  training: Training
  groupNames: string[]
  participantAvatars: PersonAvatarVariant[]
  instructorAvatars: PersonAvatarVariant[]
  contentEnabled: boolean
  hasContentModules: boolean
  isScormTraining: boolean
  hasScormFF: boolean
  companyId: number
}

const TrainingDetailHeader: FC<Props> = ({
  groupNames,
  training,
  participantAvatars,
  instructorAvatars,
  contentEnabled,
  hasContentModules,
  isScormTraining,
  hasScormFF,
  companyId,
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showContentTypeDialog, setShowContentTypeDialog] = useState(false)
  const goToTrainingSettings = () => {
    navigate(
      navigation.trainings.list.details_revamp.editTraining.view.fullPath({
        id: training.id,
      }),
      { state: { from: location.pathname } }
    )
  }

  const goToContentBuilder = () => {
    navigate(
      navigation.trainings.list.details_revamp.content.builder.fullPath({
        id: training.id,
      }),
      { state: { from: location.pathname } }
    )
  }

  const onPublish = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.startTraining.fullPath({
        id: training.id,
      })
    )
  }

  const onRevert = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.stopTraining.fullPath({
        id: training.id,
      })
    )
  }

  const groupList = groupNames

  const getTotalDuration = () => {
    const totalMinutes = training.totalDuration * 60
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)
    return `${hours}h ${minutes}m`
  }
  const closeContentTypeDialog = () => setShowContentTypeDialog(false)

  return (
    <>
      <ResourceHeader
        title={training.name}
        metadata={[
          {
            label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.type'),
            value: {
              type: 'text',
              content: training.external
                ? i18n.t('trainings.trainings_revamp.training_detail.overview.external')
                : i18n.t('trainings.trainings_revamp.training_detail.overview.internal'),
            },
          },
          ...(training.totalDuration
            ? [
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.training_detail.header.metadata.total_duration'
                  ),
                  value: {
                    type: 'text' as const,
                    content: getTotalDuration(),
                  },
                },
              ]
            : []),
          {
            label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.groups'),
            value: {
              type: 'data-list',
              data: groupList,
            },
          },
          ...(participantAvatars.length
            ? [
                {
                  label: '',
                  value: {
                    type: 'list' as const,
                    variant: 'person' as const,
                    avatars: participantAvatars,
                  },
                },
              ]
            : []),
          {
            label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.instructors'),
            value: {
              type: 'list' as const,
              variant: 'person' as const,
              avatars: instructorAvatars,
            },
          },
        ]}
        status={{
          label: '',
          text:
            training.status === 'active'
              ? i18n.t('trainings.trainings_revamp.training_detail.header.status.published')
              : i18n.t('trainings.trainings_revamp.training_detail.header.status.draft'),
          variant: training.status === 'active' ? 'positive' : 'neutral',
        }}
        primaryAction={
          training.status !== 'active'
            ? {
                label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.publish'),
                onClick: onPublish,
              }
            : undefined
        }
        secondaryActions={[
          contentEnabled &&
            !isScormTraining && {
              label: hasContentModules
                ? i18n.t('trainings.trainings_revamp.training_detail.header_buttons.edit_content')
                : i18n.t('trainings.trainings_revamp.training_detail.header_buttons.add_content'),
              icon: hasContentModules ? Pencil : Add,
              onClick:
                hasScormFF && !hasContentModules
                  ? () => setShowContentTypeDialog(true)
                  : goToContentBuilder,
            },
          training.status === 'active' && {
            label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.revert'),
            onClick: onRevert,
            variant: 'critical' as const,
            icon: Reset,
          },
        ].filter(Boolean)}
        otherActions={[
          {
            label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.settings'),
            icon: Settings,
            onClick: goToTrainingSettings,
          },
        ]}
      />
      <ContentTypeDialog
        isOpen={showContentTypeDialog}
        onClose={closeContentTypeDialog}
        onNavigateToBuilder={goToContentBuilder}
        trainingId={training.id}
        companyId={companyId}
        scormPackage={training.scormPackage}
      />
    </>
  )
}

export default TrainingDetailHeader
